import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { documentsApi } from '@/domain/documents/api';
import { currentCompanyIdSelector } from '@/domain/router/routerLeafSelectors';
import { queryOptions } from '@/services/query';

import { documentsKeys } from '@/domain/documents/queries/keys';

// #-prefixed tag/user suggestions. We only keep the hint string (the object key); the
// { score } value is dropped. companyId is in the key for per-company cache isolation
// (the CompanyId header carries scope to the backend); prefix keys + dedupes each lookup.
export const tagSuggestionsQuery = (companyId: string, prefix: string) =>
  queryOptions({
    queryKey: documentsKeys.tagSuggestions(companyId, prefix),
    queryFn: () => documentsApi.getTagSuggestions({ query: { prefix } }),
    select: (data): string[] => data.map((rec) => Object.keys(rec).join('')),
  });

// `prefix` already includes the leading `#` (verified: payload `prefix: #se`). An empty
// prefix disables the query (no request); the consumer gates on requirePrefix upstream.
export const useTagSuggestions = (prefix: string, extraEnabled = true) => {
  // companyId from Redux (not useParams): this hook runs in dialog contexts too, where the
  // route may not carry :companyId. Backend scope rides the CompanyId header — the id here
  // only isolates the cache.
  const companyId = useSelector(currentCompanyIdSelector);
  return useQuery({
    ...tagSuggestionsQuery(companyId ?? '', prefix),
    enabled: extraEnabled && Boolean(companyId) && prefix.length > 0,
  });
};
